import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import logoIcon from '../assets/logo-icon.png';

const API_BASE = '/api/v1/client-portal';

const ClientForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not send reset email.');
      }
      setMessage(data.message || 'If an account exists for that email, we have sent password reset instructions.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--background)] px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <img src={logoIcon} alt="MyMoolah" className="h-10 w-10" />
        <div>
          <p className="text-sm font-semibold text-[var(--foreground)]">MyMoolah Client Portal</p>
          <p className="text-xs text-[var(--muted-foreground)]">Forgot password</p>
        </div>
      </div>

      <div className="w-full max-w-md rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--portal-shadow)]">
        <div className="mb-2 flex items-center gap-2">
          <Mail className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          <h1 className="text-xl font-semibold text-[var(--foreground)]">Reset your password</h1>
        </div>
        <p className="mb-6 text-sm text-[var(--muted-foreground)]">
          Enter the email address registered on your Client Portal account. We will email you a secure reset link.
        </p>

        {error ? (
          <div className="mb-4 rounded-lg border border-[var(--destructive)]/20 bg-[var(--destructive)]/10 px-4 py-3 text-sm text-[var(--destructive)]" role="alert">
            {error}
          </div>
        ) : null}

        {message ? (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">
            {message}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium">Email address</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] px-4 py-2.5 text-sm"
              placeholder="you@company.co.za"
              disabled={busy || !!message}
            />
          </div>
          <button
            type="submit"
            disabled={busy || !!message}
            className="min-h-[44px] w-full rounded-[var(--radius)] bg-[var(--primary)] text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-60"
          >
            {busy ? 'Sending…' : 'Email reset link'}
          </button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-2 text-sm">
          <button type="button" onClick={() => navigate('/client/login')} className="inline-flex items-center gap-1 text-[var(--muted-foreground)] hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to sign in
          </button>
          <Link to="/client/account/password" className="text-[var(--primary)] hover:underline">
            Know your password? Change it after signing in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientForgotPasswordPage;
